GroupListView
<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <!-- Toast Notification -->
    <Toast :show="showToast" :message="toastMessage" :type="toastType" />

    <!-- Error State -->
    <div
      v-if="error"
      class="mx-4 mb-4 p-4 bg-red-100 text-red-800 rounded-lg shadow">
      {{ error }}
      <button @click="clearError" class="ml-2 underline">
        {{ $t('common.buttons.dismiss') }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <div
          class="w-12 h-12 border-4 border-[var(--color-primary-400)] border-t-[var(--color-primary-900)] rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-[var(--color-text)]">{{ $t('views.groups.loading') }}</p>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="!loading && groupsWithGoatCount.length === 0"
      class="flex-1 flex items-center justify-center px-4">
      <div class="text-center">
        <div class="text-6xl mb-4">️</div>
        <h2 class="text-xl font-bold mb-2">
          {{ $t('views.groups.noGroupsYet') }}
        </h2>
        <p class="text-[var(--color-text)] mb-4">
          {{ $t('views.groups.startOrganizing') }}
        </p>
        <button
          @click="createNewGroup"
          class="inline-block px-6 py-3 bg-[var(--color-primary-200)] text-[var(--color-primary-900)] rounded-lg border border-[var(--color-primary-400)] hover:bg-[var(--color-primary-300)] transition-colors duration-200 font-semibold">
          {{ $t('views.groups.createFirstGroup') }}
        </button>
      </div>
    </div>

    <!--Group List-->
    <div v-else class="flex-1 px-4 flex flex-col overflow-hidden pb-4">
      <ListTableModule
        :data="groupsWithGoatCount"
        :columns="tableColumns"
        :loading="loading"
        @row-click="handleRowClick"
        @refresh="refreshGroups">
        <template #cell-location="{ value }">
          <div class="flex items-center gap-1.5">
            <MapPin
              v-if="value"
              class="w-3.5 h-3.5 text-[var(--color-primary-400)]" />
            <span :class="{ 'text-gray-400 italic': !value }">
              {{ value || $t('views.groups.notSpecified') }}
            </span>
          </div>
        </template>

        <template #cell-goatCount="{ value }">
          <span
            class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200 gap-1.5">
            <Footprints class="w-3.5 h-3.5" />
            {{ value || 0 }}
          </span>
        </template>
      </ListTableModule>
    </div>
  </div>
</template>

<script setup>
  import { onMounted, ref,watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useGroupStore } from '@/stores/group.store.js'
  import { useGoatStore } from '@/stores/goat.store.js'
  import { useUiStore } from '@/stores/ui.store.js'
  import { storeToRefs } from 'pinia'
  import { PlusCircle, MapPin, Footprints } from 'lucide-vue-next'
  import ActionButton from '@/components/ui/buttons/ActionButton.vue'
  import ListTableModule from '@/components/ui/tables/ListTable.vue'
  import Toast from '@/components/ui/feedback/Toast.vue'

  const router = useRouter()
  const { t } = useI18n()

  // ==========================================
  // BACKEND INTEGRATION VIA PINIA STORE
  // ==========================================

  const groupStore = useGroupStore()
  const goatStore = useGoatStore()
  const uiStore = useUiStore()

  // Get reactive state from store (automatic UI updates!)
  const { groupsWithGoatCount, loading, error } = storeToRefs(groupStore)

  // Get actions from store
  const { fetchGroups, deleteGroup, clearError } = groupStore
  const { fetchGoats } = goatStore
  const { consumeToast } = uiStore

  // Toast notification state
  const showToast = ref(false)
  const toastMessage = ref('')
  const toastType = ref('saved')

  // Global Header Logic
  watch(groupsWithGoatCount, (groups) => {
    uiStore.setHeader({
      title: t('views.groups.title'),
      showAdd: true,
      addAction: createNewGroup 
    })
  }, { immediate: true })

  onMounted(async () => {
    if (groupsWithGoatCount.value.length === 0) await fetchGroups()
    if (goatStore.goats.length === 0) await fetchGoats()

    const pendingToast = consumeToast()
    if (pendingToast) {
      showToastNotification(pendingToast.message, pendingToast.type)
    }
  })

  //Table configuration
  const tableColumns = [
    {
      key: 'name',
      label: 'views.groups.table.name',
      type: 'text',
      simple: true,
    },
    {
      key: 'location',
      label: 'views.groups.table.location',
      type: 'text',
      simple: true,
    },
    {
      key: 'goatCount',
      label: 'views.groups.table.goatCount',
      type: 'number', // Helps sorting in ListTableModule
      simple: true,
    },
  ]

  /**
   * Show toast notification
   */
  function showToastNotification(message, type = 'saved') {
    showToast.value = false
    toastMessage.value = message
    toastType.value = type
    setTimeout(() => {
      showToast.value = true
    }, 10)
  }

  // ==========================================
  // LIFECYCLE: FETCH DATA ON MOUNT
  // ==========================================

  onMounted(async () => {
    if (groupsWithGoatCount.value.length === 0) await fetchGroups()
    // Fetch goats needed for count calculation
    if (goatStore.goats.length === 0) await fetchGoats()

    // Check for pending toast notification from UI store
    const pendingToast = consumeToast()
    if (pendingToast) {
      showToastNotification(pendingToast.message, pendingToast.type)
    }
  })

  // ==========================================
  // ACTIONS
  // ==========================================

  async function refreshGroups() {
    await Promise.all([fetchGroups(), fetchGoats()])
  }

  function handleRowClick(group) {
    // Same action as view details
    viewGroupDetails(group.id)
  }

  /**
   * Create new group
   */
  function createNewGroup() {
    console.log('Create new group clicked')
    router.push('/new-group')
  }

  /**
   * View group details (navigate to detail page or show modal)
   */
  function viewGroupDetails(groupId) {
    console.log(`Viewing group details: ${groupId}`)
    router.push(`/groups/${groupId}`)
  }

  /**
   * Delete group with confirmation
   */
  async function deleteGroupConfirm(group) {
    const confirmed = confirm(
      `${t('views.groups.deleteConfirmTitle')} "${group.name}"?\n\n` +
        t('views.groups.deleteConfirmMessage', { count: group.goatCount || 0 })
    )

    if (confirmed) {
      console.log(`Deleting group: ${group.id}`)
      const success = await deleteGroup(group.id)

      if (success) {
        console.log(`${t('views.group.deleteSuccess')}: "${group.name}"`)
        // Refresh goats to update counts automatically
        await fetchGoats()
      } else {
        console.error(`${t('views.group.deleteFailed')}: "${group.name}"`)
      }
    }
  }
</script>

<style scoped>
  /* Ensure table scrolls smoothly on mobile */
  .overflow-x-auto {
    -webkit-overflow-scrolling: touch;
  }

  /* Sticky column shadow for better UX */
  .sticky {
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
  }

  /* Loading spinner animation */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }
</style>
